import { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import axios from "axios";
import authConfig from "../../authConfig";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import FormBuilder from "./FormBuilder";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-classic/build/ckeditor";
import SecureFileLink from "../meeting/SecureFileLink";

function EditFormPopup({ formId, buttonLabel = "Edit Form", onSaved = () => { } }) {
    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // form state returned by FormBuilder.onChange
    const [formState, setFormState] = useState({
        heading: "",
        subtitle: "",
        schema: [],
        sections: [],
        sectionsubtitles: [],
        values: {},
    });

    const [pdfTemplate, setPdfTemplate] = useState("");
    const [name, setName] = useState("");
    const [emailOnSubmission, setEmailOnSubmission] = useState("");
    const [enabled, setEnabled] = useState(true);

    // submissions
    const [submissions, setSubmissions] = useState([]);
    const [loadingSubmissions, setLoadingSubmissions] = useState(false);
    const [selectedSubmissionJson, setSelectedSubmissionJson] = useState(null);

    const resetState = () => {
        setFormState({
            heading: "",
            subtitle: "",
            schema: [],
            sections: [],
            sectionsubtitles: [],
            values: {},
        });
        setPdfTemplate("");
        setName("");
        setEnabled(true);
        setSubmissions([]);
        setError(null);
    };

    const handleShow = () => {
        if (!formId) {
            setError("No form selected");
            return;
        }
        loadForm();
        setShow(true);
        loadSubmissions();
    };
    const handleClose = () => {
        setShow(false);
        resetState();
    };

    async function acquireAxios() {
        if (!isAuthenticated || !account) {
            // Return a default axios instance (may rely on cookie auth)
            return axios.create();
        }
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return axios.create({
                headers: { Authorization: `Bearer ${response.accessToken}` }
            });
        } catch (ex) {
            // fallback to unauthenticated axios
            return axios.create();
        }
    }

    const loadForm = async () => {
        setError(null);
        setLoading(true);
        try {
            const client = await acquireAxios();
            const res = await client.get(authConfig.api.urls.getMemberForm + "/" + formId);
            const data = res.data;
            setName(data.name || "");
            setEnabled(data.enabled ?? true);
            setEmailOnSubmission(data.emailOnSubmission || "");
            setFormState({
                heading: data.heading || "",
                subtitle: data.subHeading || "",
                schema: data.formSchemaJson ? JSON.parse(data.formSchemaJson) : [],
                sections: data.sectionsJson ? JSON.parse(data.sectionsJson) : [],
                sectionsubtitles: data.sectionSubtitlesJson ? JSON.parse(data.sectionSubtitlesJson) : [],
                values: data.valuesJson ? JSON.parse(data.valuesJson) : {}
            });
            setPdfTemplate(data.pdfTemplate || "");
        } catch (err) {
            console.error(err);
            setError("Error loading form: " + (err?.message ?? err));
        } finally {
            setLoading(false);
        }
    };

    const saveForm = async () => {
        setError(null);
        setLoading(true);
        try {
            const client = await acquireAxios();
            const payload = {
                Id: formId,
                Name: name,
                EmailOnSubmission: emailOnSubmission,
                Enabled: enabled,
                Heading: formState.heading || "",
                SubHeading: formState.subtitle || "",
                FormSchemaJson: JSON.stringify(formState.schema || []),
                SectionsJson: JSON.stringify(formState.sections || []),
                SectionSubtitlesJson: JSON.stringify(formState.sectionsubtitles || []),
                ValuesJson: JSON.stringify(formState.values || {}),
                PdfTemplate: pdfTemplate || ""
            };
            await client.post(authConfig.api.urls.getMemberFormSave, payload);
            onSaved();
            setShow(false);
        } catch (err) {
            console.error(err);
            setError("Error saving form: " + (err?.message ?? err));
        } finally {
            setLoading(false);
        }
    };

    const loadSubmissions = async () => {
        setLoadingSubmissions(true);
        try {
            const client = await acquireAxios();
            const res = await client.get(authConfig.api.urls.getMemberFormSubmissions + "/" + formId);
            setSubmissions(res.data || []);
        } catch (err) {
            console.error(err);
            setError("Error loading submissions: " + (err?.message ?? err));
        } finally {
            setLoadingSubmissions(false);
        }
    };

    const openSubmissionJson = (json) => {
        try {
            const parsed = typeof json === "string" ? JSON.parse(json) : json;
            setSelectedSubmissionJson(JSON.stringify(parsed, null, 2));
        } catch {
            setSelectedSubmissionJson(json);
        }
    };

    return (
        <>
            <Button variant="success" onClick={handleShow} disabled={!formId}>
                {buttonLabel}
            </Button>

            <Modal show={show} onHide={handleClose} size="xl" centered dialogClassName="edit-form-popup" autoFocus={false} enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Form {name ? `- ${name}` : ""}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {loading ? (
                        <div className="text-center"><Spinner animation="border" /></div>
                    ) : (
                            <Tabs defaultActiveKey="form" id="edit-form-tabs" className="mb-3 memberformeditortabs">
                            <Tab eventKey="form" title="Form">
                                <div className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Form name</Form.Label>
                                        <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
                                    </Form.Group>
                                    <Form.Check
                                        className="mt-2"
                                        type="switch"
                                        id="form-enabled-switch"
                                        label={enabled ? "Enabled" : "Disabled"}
                                        checked={enabled}
                                        onChange={(e) => setEnabled(e.target.checked)}
                                    />
                                    <Form.Group>
                                        <Form.Label className="mt-2">On submission Email</Form.Label>
                                        <Form.Control type="email" value={emailOnSubmission} onChange={(e) => setEmailOnSubmission(e.target.value)} placeholder="Enter email to notify on each submission" />
                                    </Form.Group>
                                </div>
                                <hr />

                            </Tab>
                            <Tab eventKey="formtemplate" title="Form Template">
                                {formState ? (
                                    <FormBuilder
                                        formState={formState}
                                        onChange={(fs) => {
                                            // FormBuilder calls onChange with full state
                                            setFormState({
                                                heading: fs.heading,
                                                subtitle: fs.subtitle,
                                                schema: fs.schema,
                                                sections: fs.sections,
                                                sectionsubtitles: fs.sectionsubtitles,
                                                values: fs.values
                                            });
                                        }}
                                    />
                                ) : (<></>)}
                            </Tab>
                            <Tab eventKey="pdf" title="PDF Template">
                                <Form.Group className="mb-2">
                                    <div className="mb-2">
                                        The following will be replaced by meeting relative text;<br />
                                        &#123;membername&#125; &#123;submissiondate&#125; &#123;memberfirstname&#125; &#123;membersurname&#125; &#123;memberemail&#125;
                                        {formState.schema.map(o => (
                                            <span>&#123;{o.name}&#125; </span>
                                        ))}
                                    </div>
                                    <div>
                                        <CKEditor
                                            editor={Editor}
                                            data={pdfTemplate || ""}
                                            onReady={editor => {
                                                // editor ready
                                            }}
                                            onChange={(event, editor) => {
                                                const html = editor.getData();
                                                setPdfTemplate(html);
                                            }}
                                        />
                                    </div>
                                </Form.Group>
                            </Tab>

                            <Tab eventKey="submissions" title={`Submissions (${submissions?.length || 0})`}>
                                {loadingSubmissions ? (
                                    <div className="text-center"><Spinner animation="border" /></div>
                                ) : (
                                    <>
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Member</th>
                                                    <th>Submitted On</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {submissions.map((s, i) => (
                                                    <tr key={s.id || s.Id || i}>
                                                        <td>{i + 1}</td>
                                                        <td>{s.memberName || s.MemberName || ""}</td>
                                                        <td>{s.submittedOn || s.SubmittedOn || ""}</td>
                                                        <td>
                                                            <Button size="sm" variant="primary" className="me-1"
                                                                onClick={() => openSubmissionJson(s.submissionDataJson || s.SubmissionDataJson || "{}")}>
                                                                View JSON
                                                            </Button>
                                                            <SecureFileLink
                                                                type="button"
                                                                url={authConfig.api.urls.getMemberFormSubmissionPdf + "/" + (s.id || s.Id) + "/submission.pdf?dt=" + (new Date()).getTime()}
                                                                filename={"submission.pdf"}>View PDF
                                                            </SecureFileLink>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        <Modal show={selectedSubmissionJson !== null} onHide={() => setSelectedSubmissionJson(null)} size="lg" centered>
                                            <Modal.Header closeButton>
                                                <Modal.Title>Submission JSON</Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body>
                                                <Form.Control as="textarea" rows={18} readOnly value={selectedSubmissionJson || ""} />
                                            </Modal.Body>
                                            <Modal.Footer>
                                                <Button variant="secondary" onClick={() => setSelectedSubmissionJson(null)}>Close</Button>
                                            </Modal.Footer>
                                        </Modal>
                                    </>
                                )}
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button variant="success" onClick={saveForm} disabled={loading}>
                        {loading ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default EditFormPopup;